/**
 * What has to exist before the first deploy.
 *
 * Placed immediately after the hero on purpose. This is the single most
 * load-bearing fact about the tool — it attaches to infrastructure you already
 * own — and someone who discovers it three sections in has already formed the
 * wrong expectation.
 */
const NEVER_CREATED: Array<[string, string]> = [
  ['VPC and subnets', 'Referenced by ID. Where tasks run.'],
  ['ECS cluster', 'Referenced by name. `aws ecs create-cluster` is enough — nothing else needed.'],
  [
    'Application Load Balancer',
    'Referenced by listener ARN. A listener can be created on one you own, but the load balancer itself never is.',
  ],
  ['ACM certificate', 'Referenced by ARN, for an HTTPS listener.'],
  ['Secrets Manager / SSM entries', 'Referenced by ARN. Values are never read by the deployer.'],
];

const CREATED_IF_OMITTED: Array<[string, string]> = [
  [
    'Task security group',
    'Created with all egress allowed and no ingress except the load balancer rule. Supply your own to keep control.',
  ],
  [
    'Task and execution IAM roles',
    'Created with exactly what the manifest needs — image pull, log writes, read access to the secrets you listed, ECS Exec.',
  ],
];

export function Prereqs() {
  return (
    <section id="prerequisites">
      <div className="callout">
        <h2 style={{ marginTop: 0 }}>This deploys into infrastructure you already have</h2>
        <p className="lede" style={{ marginBottom: 0 }}>
          It is not a from-scratch environment builder. A VPC, an ECS cluster and (for a
          load-balanced service) an Application Load Balancer with a listener must already exist —
          the deployer attaches to them and never modifies them.
        </p>
      </div>

      <h3 style={{ marginTop: '2rem' }}>You bring these. They are never created or changed.</h3>
      <div className="table-wrap">
        <table>
          <tbody>
            {NEVER_CREATED.map(([what, note]) => (
              <tr key={what}>
                <td style={{ width: '30%' }}>
                  <strong>{what}</strong>
                </td>
                <td className="muted">{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: '2rem' }}>These are created for you, but only if you omit them.</h3>
      <div className="table-wrap">
        <table>
          <tbody>
            {CREATED_IF_OMITTED.map(([what, note]) => (
              <tr key={what}>
                <td style={{ width: '30%' }}>
                  <strong>{what}</strong>
                </td>
                <td className="muted">{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="small muted" style={{ marginTop: '1.25rem' }}>
        There is one deliberate exception to “never modified”: when you give{' '}
        <code>loadBalancer.securityGroupId</code>, an ingress rule is added to the task security
        group so the load balancer can reach the container port. Set{' '}
        <code>manageSecurityGroupRules: false</code> to manage that yourself.
      </p>

      <p className="small muted">
        Starting with an empty account?{' '}
        <a href="https://github.com/FutureMoney/fargate-deployer/blob/main/docs/aws-setup.md">
          docs/aws-setup.md
        </a>{' '}
        covers creating the prerequisites, bootstrapping CDK, and the deploy role.
      </p>
    </section>
  );
}
